import type { Component } from '../router';
import { renderBuildStamp } from '../components/BuildStamp';
import { ApiError } from '../services/api';
import { requestMagicLink, startOAuth, getAuthErrorMessage, type OAuthProvider } from '../services/auth';

type SubmitState = 'idle' | 'sending' | 'sent' | 'error';

const SVG_NS = 'http://www.w3.org/2000/svg';

// Brand marks built as elements (not innerHTML), matching components/icons.ts.
// Google is its fixed four-colour glyph; Apple is a single path filled with
// currentColor so it tracks the button's text colour (white on the dark button
// in light mode, dark on the white button in dark mode).
const BRAND_PATHS: Record<OAuthProvider, { d: string; fill: string }[]> = {
  google: [
    { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z', fill: '#4285F4' },
    { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' },
    { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' },
    { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' },
  ],
  apple: [
    { d: 'M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z', fill: 'currentColor' },
  ],
};

function brandMark(provider: OAuthProvider): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '18');
  svg.setAttribute('height', '18');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('btn-oauth__mark');
  for (const { d, fill } of BRAND_PATHS[provider]) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    path.setAttribute('fill', fill);
    svg.appendChild(path);
  }
  return svg;
}

function oauthButton(provider: OAuthProvider, label: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `btn-oauth btn-oauth--${provider}`;
  btn.dataset.provider = provider;
  btn.appendChild(brandMark(provider));
  const span = document.createElement('span');
  span.className = 'btn-oauth__label';
  span.textContent = label;
  btn.appendChild(span);
  btn.addEventListener('click', () => {
    // A full-page navigation to the provider follows; disable so a second
    // click can't fire a duplicate init while the browser is leaving.
    btn.disabled = true;
    startOAuth(provider);
  });
  return btn;
}

export function errorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Too many attempts — give it a minute.';
    return 'Something went wrong on our end. Please try again.';
  }
  return 'Could not reach the server. Check your connection.';
}

export class LoginPage implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const section = document.createElement('section');
    section.className = 'login';

    const card = document.createElement('div');
    card.className = 'login__card';

    const brand = document.createElement('div');
    brand.className = 'login__brand';
    const logo = document.createElement('img');
    logo.src = '/brand/logo.png';
    logo.alt = 'Bluefield Chemicals';
    logo.width = 360;
    logo.height = 166;
    brand.appendChild(logo);

    const title = document.createElement('h1');
    title.className = 'login__title';
    title.textContent = 'Sign in';

    const sub = document.createElement('p');
    sub.className = 'login__sub';
    sub.textContent = 'Enter your email — we’ll send you a magic link.';

    const form = document.createElement('form');
    form.className = 'login__form';
    form.noValidate = true;

    const label = document.createElement('label');
    label.className = 'login__label';
    label.htmlFor = 'login-email';
    label.textContent = 'Email';

    const input = document.createElement('input');
    input.className = 'login__input';
    input.id = 'login-email';
    input.name = 'email';
    input.type = 'email';
    input.required = true;
    input.placeholder = 'name@example.com';
    input.autocomplete = 'email';
    input.spellcheck = false;
    input.autocapitalize = 'none';
    input.enterKeyHint = 'send';
    input.setAttribute('autocorrect', 'off');

    const submit = document.createElement('button');
    submit.className = 'login__submit';
    submit.type = 'submit';
    submit.dataset.state = 'idle';
    const submitLabel = document.createElement('span');
    submitLabel.className = 'login__submit-label';
    submitLabel.textContent = 'Send magic link';
    submit.appendChild(submitLabel);

    const status = document.createElement('p');
    status.className = 'login__status';
    status.dataset.visible = 'false';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const footnote = document.createElement('p');
    footnote.className = 'login__footnote';
    footnote.textContent = 'We’ll never share your email.';

    const setState = (next: SubmitState, message?: string, tone?: 'success' | 'error'): void => {
      submit.dataset.state = next;
      // Lock the button in 'sending' AND 'sent' so a second click can't
      // fire a duplicate magic-link send. 'error' stays clickable so the
      // user can retry without reloading.
      submit.disabled = next === 'sending' || next === 'sent';
      submitLabel.textContent = next === 'sent' ? 'Check your inbox' : 'Send magic link';
      if (message) {
        status.textContent = message;
        status.dataset.visible = 'true';
        if (tone) status.dataset.tone = tone;
        else delete status.dataset.tone;
      } else {
        status.dataset.visible = 'false';
      }
    };

    // When the user edits the email field after a 'sent' or 'error',
    // reset to idle so they can re-submit (e.g. a typo correction).
    // No timeout-based revert: that's what produced the "second click =
    // duplicate email" bug.
    input.addEventListener('input', () => {
      const s = submit.dataset.state;
      if (s === 'sent' || s === 'error') {
        setState('idle');
      }
    });

    // Keyboard assist for short viewports (SE-class phones): once the
    // on-screen keyboard has settled, nudge the field to mid-viewport so
    // it and the submit button clear the keyboard + accessory bar. The
    // activeElement guard skips the scroll if focus moved on (or the
    // page unmounted) before the timer fired.
    input.addEventListener('focus', () => {
      window.setTimeout(() => {
        if (document.activeElement === input && typeof input.scrollIntoView === 'function') {
          input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 300);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      // Belt-and-suspenders: if the button is already disabled for any
      // reason, ignore the submit (covers Enter-key paths around the
      // disabled state).
      if (submit.disabled) return;
      setState('sending', 'Sending…');
      try {
        await requestMagicLink(input.value);
        // Server returns 200 for any email per enumeration policy. The
        // button stays locked in 'sent' until the user edits the email
        // — see the input listener above.
        setState('sent', 'If this email is registered, a link is on its way to your inbox.', 'success');
      } catch (err) {
        // Enumeration policy means happy-path /api/auth/magic-link returns
        // 200 for any email — but CSRF (403), rate limit (429), and server
        // faults (5xx) all still surface as ApiError. Reporting all of them
        // as "could not reach the server" sent #77 down the wrong path.
        setState('error', errorCopy(err), 'error');
      }
    });

    form.append(label, input, submit, status, footnote);

    // OAuth alternative below the magic-link form.
    const divider = document.createElement('div');
    divider.className = 'login__divider';
    divider.setAttribute('role', 'separator');
    const dividerLabel = document.createElement('span');
    dividerLabel.textContent = 'or';
    divider.appendChild(dividerLabel);

    const oauth = document.createElement('div');
    oauth.className = 'login__oauth';
    oauth.append(oauthButton('apple', 'Continue with Apple'), oauthButton('google', 'Continue with Google'));

    // Lost-passkey recovery entry point. A logged-out user who can't
    // complete a sensitive op because their passkey is gone starts here; the
    // router intercepts the anchor for client-side navigation.
    const recovery = document.createElement('p');
    recovery.className = 'login__recovery';
    const recoveryLink = document.createElement('a');
    recoveryLink.href = '/recovery';
    recoveryLink.textContent = 'Lost your passkey?';
    recovery.appendChild(recoveryLink);

    const cardChildren: HTMLElement[] = [brand, title, sub];

    // An OAuth callback failure is forwarded here as /login?error=CODE (app.ts
    // rewrites the provider's ?auth=error&code=CODE landing). Surface it above
    // the form, where the "use a magic link instead" copy points at the form.
    const errorCode = new URLSearchParams(window.location.search).get('error');
    if (errorCode) {
      const banner = document.createElement('p');
      banner.className = 'login__status login__alert';
      banner.dataset.visible = 'true';
      banner.dataset.tone = 'error';
      banner.setAttribute('role', 'alert');
      banner.textContent = getAuthErrorMessage(errorCode);
      cardChildren.push(banner);
    }

    cardChildren.push(form, divider, oauth, recovery);
    card.append(...cardChildren);
    renderBuildStamp(card);
    section.appendChild(card);
    host.appendChild(section);
    this.root = section;

    // Focus email field on mount so keyboard users can start typing.
    queueMicrotask(() => input.focus());
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}
